import {ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState} from "react";
import {NetworkCalls} from "../NetworkCalls";
import {Organisation} from "../dtos/Organisation";
import {useNavigate} from "react-router-dom";
import {NetworkErrorHandling} from "../components/NetworkErrorHandling";
import {AxiosError} from "axios";
import FishProtectionsView from "./water-administration/FishProtectionsView";
import {OrgaFishMaintainDTO} from "../dtos/OrgaFishMaintainDTO";
import {useSelectedOrganisationContext} from "../custom-hooks/useOrganisation";

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [toast, setToast] = useState<
    {type: "success" | "error"; message: string} | null
  >(null);
  const [organisation, setOrganisation] = useState<Organisation | undefined>(
    undefined
  );

  const [activeSection, setActiveSection] = useState<
    "general" | "fish-occurrence" | "protections"
  >("general");
  const [fishList, setFishList] = useState<OrgaFishMaintainDTO[]>([]);
  const [fishLoading, setFishLoading] = useState(false);
  const [fishError, setFishError] = useState<string | undefined>(undefined);
  const [fishSaving, setFishSaving] = useState(false);

  const { selectedOrganisation } = useSelectedOrganisationContext();

  useEffect(() => {
    if (!selectedOrganisation) {
      return;
    }

    if (!selectedOrganisation.roles?.includes("ROLE_ORGANISATION_ADMINISTRATOR")) {
      navigate("/dashboard");
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await NetworkCalls.getOrganisationDetails(
          selectedOrganisation.id
        );
        if (data) {
          setOrganisation(data);
        } else {
          setError("Organisationsdaten konnten nicht geladen werden.");
        }
      } catch (err) {
        NetworkErrorHandling.handle(err as AxiosError);
        setError("Organisationsdaten konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    };

    fetchData().catch(console.error);
  }, [navigate, selectedOrganisation]);

  const fetchFishes = useCallback(async () => {
    if (!selectedOrganisation) {
      setFishList([]);
      return;
    }
    try {
      setFishLoading(true);
      setFishError(undefined);
      const fishNameResult = await NetworkCalls.getFishNames("DE");
      const names = Array.isArray(fishNameResult) ? fishNameResult : [];
      let maintainList: OrgaFishMaintainDTO[] = [];
      try {
        const maintainResult =
          await NetworkCalls.getMaintainFishByOrganisation(selectedOrganisation.id);
        maintainList = Array.isArray(maintainResult) ? maintainResult : [];
      } catch (err) {
        console.error(err);
      }
      const maintainMap = new Map(
        maintainList.map((item) => [item.fishId, item])
      );
      const maintainLabelMap = new Map(
        maintainList.map((item) => {
          const label = (item as OrgaFishMaintainDTO & {label?: string})
            .label;
          const key = (label ?? item.name ?? "").toLowerCase();
          return [key, item];
        })
      );
      const nextList = names
        .map((item) => {
          const fishId = item.fishId ?? item.fish?.id;
          if (!fishId) {
            return null;
          }
          const maintain = maintainMap.get(fishId);
          const maintainByLabel = maintain
            ? maintain
            : maintainLabelMap.get(item.name.toLowerCase());
          const result: OrgaFishMaintainDTO = {
            fishId,
            orgaFishId: maintainByLabel?.orgaFishId ?? null,
            name: item.name,
            available: maintainByLabel?.available ?? false
          };
          if (maintainByLabel?.label) {
            result.label = maintainByLabel.label;
          }
          return result;
        })
        .filter((item): item is OrgaFishMaintainDTO => item !== null);
      setFishList(nextList);
    } catch (err) {
      NetworkErrorHandling.handle(err as AxiosError);
      setFishError("Fischvorkommen konnten nicht geladen werden.");
    } finally {
      setFishLoading(false);
    }
  }, [selectedOrganisation]);

  useEffect(() => {
    if (activeSection !== "fish-occurrence") {
      return;
    }
    fetchFishes().catch(console.error);
  }, [activeSection, fetchFishes]);

  useEffect(() => {
    if (!selectedOrganisation) {
      setFishList([]);
    }
  }, [selectedOrganisation]);

  useEffect(() => {
    if (!toast) {
      return;
    }
    const timer = setTimeout(() => {
      setToast(null);
    }, 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {name, value} = event.target;
    setOrganisation((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        [name]: value
      };
    });
  };

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setError(undefined);
    try {
      setLoading(true);
      if (!organisation) {
        setError("Organisationsdaten fehlen.");
        return;
      }
      const updated = await NetworkCalls.updateOrganisationDetails(organisation);
      setOrganisation(updated ?? organisation);
      setToast({type: "success", message: "Vereinsdaten gespeichert."});
    } catch (err) {
      NetworkErrorHandling.handle(err as AxiosError);
      setError("Speichern fehlgeschlagen.");
      setToast({type: "error", message: "Speichern fehlgeschlagen."});
    } finally {
      setLoading(false);
    }
  };

  const memberReportLink = organisation?.organisationToken
    ? `${window.location.origin}/catch-report/${organisation.organisationToken}/`
    : "";

  const copyMemberReportLink = async () => {
    if (!memberReportLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(memberReportLink);
      setToast({type: "success", message: "Link kopiert."});
    } catch {
      setToast({type: "error", message: "Kopieren fehlgeschlagen."});
    }
  };

  const sectionTitle = useMemo(() => {
    switch (activeSection) {
      case "fish-occurrence":
        return "Fischvorkommen";
      case "protections":
        return "Schonzeiten & Entnahmefenster";
      default:
        return "Allgemein";
    }
  }, [activeSection]);

  const toggleFish = (fishId: string) => {
    const current = Array.isArray(fishList) ? fishList : [];
    const nextList = current.map((fish) =>
      fish.fishId === fishId
        ? {...fish, available: !fish.available}
        : fish
    );
    setFishList(nextList);
    if (!selectedOrganisation) {
      setFishError("Keine Organisation ausgewählt.");
      return;
    }
    setFishSaving(true);
    setFishError(undefined);
    const target = nextList.find((fish) => fish.fishId === fishId);
    const deleteId = target?.orgaFishId ?? fishId;
    const action = target?.available
      ? NetworkCalls.addOrgaFish(selectedOrganisation.id, fishId)
      : NetworkCalls.removeOrgaFish(selectedOrganisation.id, deleteId);
    action
      .catch((err) => {
        NetworkErrorHandling.handle(err as AxiosError);
        setFishError("Speichern fehlgeschlagen.");
        setFishList(current);
      })
      .finally(() => {
        setFishSaving(false);
        fetchFishes().catch(console.error);
      });
  };

  return (
    <div className="container-xl">
      {toast ? (
        <div
          className="position-fixed end-0 p-3"
          style={{zIndex: 1080, top: "7rem"}}
        >
          <div
            className={`alert ${
              toast.type === "success" ? "alert-success" : "alert-danger"
            } alert-dismissible shadow-lg border-2 px-4 py-3 fs-5 fw-semibold`}
            role="alert"
            style={{minWidth: "380px"}}
          >
            <div className="d-flex align-items-start justify-content-between">
              <div>{toast.message}</div>
              <button
                type="button"
                className="btn-close ms-2"
                aria-label="Close"
                onClick={() => setToast(null)}
              />
            </div>
          </div>
        </div>
      ) : null}
      <div className="page-header d-print-none">
        <h2 className="page-title">Vereinsdaten</h2>
      </div>
      <div className="row g-3">
        <div className="col-12 col-md-3">
          <div className="card">
            <div className="list-group list-group-flush">
              <button
                type="button"
                className={`list-group-item list-group-item-action${
                  activeSection === "general" ? " active" : ""
                }`}
                onClick={() => setActiveSection("general")}
              >
                Allgemein
              </button>
              <button
                type="button"
                className={`list-group-item list-group-item-action${
                  activeSection === "fish-occurrence" ? " active" : ""
                }`}
                onClick={() => setActiveSection("fish-occurrence")}
              >
                Fischvorkommen
              </button>
              <button
                type="button"
                className={`list-group-item list-group-item-action${
                  activeSection === "protections" ? " active" : ""
                }`}
                onClick={() => setActiveSection("protections")}
              >
                Schonzeiten &amp; Entnahmefenster
              </button>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-9">
          <div className="card">
            <div className="card-body">
              <h3 className="mb-4">{sectionTitle}</h3>
              {activeSection === "general" ? (
                <form onSubmit={save} autoComplete="off">
                  <span className={!error ? "d-none" : "d-block badge-error"}>
                    {error}
                  </span>
                  <div className="mb-3">
                    <label className="form-label text-dark">Vereinsname</label>
                    <input
                      type="text"
                      className="form-control"
                      name="organisationName"
                      value={organisation?.organisationName ?? ""}
                      onChange={handleChange}
                      disabled={loading || !organisation}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-dark">Straße</label>
                    <input
                      type="text"
                      className="form-control"
                      name="street"
                      value={organisation?.street ?? ""}
                      onChange={handleChange}
                      disabled={loading || !organisation}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-dark">PLZ</label>
                    <input
                      type="text"
                      className="form-control"
                      name="zipcode"
                      value={organisation?.zipcode ?? ""}
                      onChange={handleChange}
                      disabled={loading || !organisation}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-dark">Stadt</label>
                    <input
                      type="text"
                      className="form-control"
                      name="city"
                      value={organisation?.city ?? ""}
                      onChange={handleChange}
                      disabled={loading || !organisation}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-dark">Telefon</label>
                    <input
                      type="text"
                      className="form-control"
                      name="telephoneNumber"
                      value={organisation?.telephoneNumber ?? ""}
                      onChange={handleChange}
                      disabled={loading || !organisation}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-dark">E-Mail</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={organisation?.email ?? ""}
                      onChange={handleChange}
                      disabled={loading || !organisation}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-dark">Organisations-Token</label>
                    <input
                      type="text"
                      className="form-control"
                      value={organisation?.organisationToken ?? ""}
                      disabled
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-dark">
                      Fangmeldungen-Link (Mitglied)
                    </label>
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control"
                        value={memberReportLink}
                        disabled
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary rounded"
                        onClick={copyMemberReportLink}
                        disabled={!memberReportLink}
                      >
                        Kopieren
                      </button>
                    </div>
                  </div>
                  <div className="d-grid gap-2">
                    <button
                      className="btn btn-primary fk-color"
                      type="submit"
                      disabled={loading || !organisation}
                    >
                      Speichern
                    </button>
                  </div>
                </form>
              ) : null}
              {activeSection === "fish-occurrence" ? (
                <div>
                  {fishLoading ? (
                    <div className="text-muted">Lade Fischvorkommen...</div>
                  ) : null}
                  {fishError ? (
                    <div className="text-danger mb-3">{fishError}</div>
                  ) : null}
                  {!fishLoading ? (
                    <>
                      {Array.isArray(fishList) && fishList.length === 0 ? (
                        <div className="text-muted">
                          Keine Fischarten gefunden.
                        </div>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {[...(Array.isArray(fishList) ? fishList : [])]
                            .sort((a, b) =>
                              (a.name ?? "").localeCompare(b.name ?? "", "de", {
                                sensitivity: "base"
                              })
                            )
                            .map((fish) => (
                              <label
                                key={fish.fishId}
                                className="form-check form-check-inline"
                              >
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={fish.available}
                                  onChange={() => toggleFish(fish.fishId)}
                                  disabled={fishSaving}
                                />
                                <span className="form-check-label">
                                  {fish.name}
                                </span>
                              </label>
                            ))}
                        </div>
                      )}
                      {fishSaving ? (
                        <div className="text-muted mt-3">Speichere...</div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              ) : null}
              {activeSection === "protections" ? (
                <FishProtectionsView embedded />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
